import { SvgIcon } from '@progress/kendo-react-common';
import clsx from 'clsx';
import React, { useRef } from 'react';
import { CheckmarkOutline, WarningTriangleOutline } from '../../../../../../../icons/icons';
import { AircraftThresholdMergeResult } from '../../../../../../../monitoring/merger/aircraft-threshold-merger';
import { AirportThresholdMergeResult } from '../../../../../../../monitoring/merger/airport-threshold-merger';
import { CompanyThresholdsMode, ThresholdControlType, ThresholdInfo, ThresholdLevel } from '../../../../../../../types/thresholds';
import { AlertRepeatsIconIcon, AlertTriggerImprovingIcon } from './AlertIcons';
import { levelToKeyMap } from './common';

export type ThresholdLevelClickedArgs = {
    level: ThresholdLevel;
    element: HTMLElement;
};

export const SettingsThresholdsLevel = React.memo(
    (props: {
        info: ThresholdInfo;
        readonly: boolean;
        mode: CompanyThresholdsMode;
        context: AircraftThresholdMergeResult | AirportThresholdMergeResult;
        level: ThresholdLevel;
        onClick?: (args: ThresholdLevelClickedArgs) => void;
    }) => {
        const ref = useRef<HTMLDivElement>(null);
        const info = props.info;
        const levelKey = levelToKeyMap[props.level];
        const level = props.context.effective?.[levelKey];

        const faded = false;
        // TODO: TBD do we do faded? idk
        //            props.mode === CompanyThresholdsMode.template && props.context.definition?.ignoredInheritedLevels.includes(props.level);
        const bold = props.context.effective?.[levelKey].isOverride;

        let value: React.ReactNode = '-';
        if (level?.value) {
            switch (info.controlType) {
                case ThresholdControlType.dropdown:
                    value = info.dropdownOptions[level.value.value] || '-';
                    break;
                case ThresholdControlType.checkbox:
                    value = <SvgIcon icon={CheckmarkOutline} />;
                    break;
                case ThresholdControlType.input: {
                    const valueString = valueToString(level.value.value, info.decimal);
                    if (info.customUnits.length > 0) {
                        const unit = info.customUnits[level.value.unitIndex ?? 0];
                        value = (
                            <>
                                {valueString} {unit?.displayName}
                            </>
                        );
                    } else {
                        value = (
                            <>
                                {valueString} {info.units}
                            </>
                        );
                    }
                    break;
                }
            }
        }

        return (
            <div
                className={clsx('d-flex gap-1 align-items-center', bold && 'fw-bold', faded && 'opacity-50', !props.readonly && 'fe-hover')}
                ref={ref}
                onClick={() => !props.readonly && props.onClick?.({ level: props.level, element: ref.current! })}>
                {level && level.errors > 0 && <SvgIcon icon={WarningTriangleOutline} themeColor="error" />}
                <span>{value}</span>
                <span>
                    <AlertRepeatsIconIcon show={info.canRepeat && level?.value?.repeat} />
                    <AlertTriggerImprovingIcon show={level?.value?.triggerOnImproving} />
                </span>
            </div>
        );
    }
);

function valueToString(value: number, decimal: boolean): string {
    if (decimal) {
        value /= 100;
        const trunc = Math.trunc(value);
        return trunc.toLocaleString('en-US') + (value - trunc).toFixed(2).slice(1);
    }

    return value.toLocaleString('en-US');
}
